export const DEFAULT_PRICING_CONFIG_BODY = {
    universal: {
        sizeUpcharge: { xxl: 1.5, xxxl: 2.5 },
    },
    screenPrint: {
        minQty: 48,
        cpcTiers: [
            { minQty: 500, cpc: 0.08 },
            { minQty: 250, cpc: 0.12 },
            { minQty: 100, cpc: 0.18 },
            { minQty: 48, cpc: 0.25 },
            { minQty: 0, cpc: 0.35 },
        ],
        extraColorUpcharge: [
            { minQty: 500, upcharge: 0.4 },
            { minQty: 250, upcharge: 0.75 },
            { minQty: 100, upcharge: 1.0 },
            { minQty: 0, upcharge: 1.5 },
        ],
        locationUpcharge: [
            { minQty: 500, upcharge: 1.0 },
            { minQty: 250, upcharge: 1.25 },
            { minQty: 100, upcharge: 1.5 },
            { minQty: 0, upcharge: 1.75 },
        ],
        internalLocationCost: 1.0,
        setupTiers: [
            { minQty: 100, setup: 0.0 },
            { minQty: 48, setup: 25.0 },
            { minQty: 0, setup: 50.0 },
        ],
        wholesalePrintTiers: [
            { minQty: 1500, rate: 1.1 },
            { minQty: 1000, rate: 1.3 },
            { minQty: 750, rate: 1.7 },
            { minQty: 500, rate: 2.25 },
            { minQty: 250, rate: 2.65 },
            { minQty: 100, rate: 4.0 },
            { minQty: 48, rate: 5.35 },
            { minQty: 0, rate: 8.05 },
        ],
        retailPrintTiers: {
            colored: [
                { minQty: 500, rate: 5.15 },
                { minQty: 250, rate: 6.55 },
                { minQty: 100, rate: 6.9 },
                { minQty: 48, rate: 8.25 },
                { minQty: 0, rate: 10.75 },
            ],
            white: [
                { minQty: 500, rate: 4.0 },
                { minQty: 250, rate: 4.35 },
                { minQty: 100, rate: 4.65 },
                { minQty: 48, rate: 6.0 },
                { minQty: 0, rate: 7.8 },
            ],
        },
        floorTiers: [
            { minQty: 1500, floor: 0.8 },
            { minQty: 1000, floor: 0.95 },
            { minQty: 750, floor: 1.25 },
            { minQty: 500, floor: 1.65 },
            { minQty: 250, floor: 1.95 },
            { minQty: 100, floor: 2.95 },
            { minQty: 48, floor: 3.95 },
            { minQty: 0, floor: 5.95 },
        ],
        darkUnderbase: { qtyBreak: 500, below: 1.0, atOrAbove: 0.5 },
        rushMultipliers: { none: 1, rush: 1.25, hot_rush: 1.5 },
    },
    dtfTransfer: {
        sheetWidthIn: 35,
        internalCostPerSqIn: 0.0035,
        baseRateTiers: [
            { maxLength: 12, rate: 0.04 },
            { maxLength: 24, rate: 0.036 },
            { maxLength: 36, rate: 0.032 },
            { maxLength: 60, rate: 0.03 },
            { maxLength: 120, rate: 0.028 },
            // Last tier: unbounded for any practical input. Stored as
            // MAX_SAFE_INTEGER (functionally infinite, JSON-serializable for
            // DDB) instead of Infinity (which Zod 4 rejects in z.number()).
            // Input schema caps lengthIn at 1200 so this sentinel is never
            // approached by valid traffic.
            { maxLength: Number.MAX_SAFE_INTEGER, rate: 0.026 },
        ],
        b2bMultiplier: 0.88,
        b2bMinRate: 0.024,
        etsyDefaultRate: 0.05,
        etsyMultiplier: 0.9,
        floor: { b2b: 8.0, b2c: 10.0, etsy: 10.0 },
    },
    dtfPressAndPress: {
        transferCostPerSqIn: 0.0035,
        laborPerLocationDefault: 1.25,
        minSellBeforeLaborBlank: 3.0,
        marginTiers: [
            { minQty: 100, marginPct: 30 },
            { minQty: 48, marginPct: 35 },
            { minQty: 24, marginPct: 45 },
            { minQty: 12, marginPct: 55 },
            { minQty: 0, marginPct: 65 },
        ],
        marginWarnFloorPct: 35,
        rateWarn: { red: 0.02, amber: 0.035 },
    },
    dtg: {
        hardMaxQty: 48,
        softMaxQty: 24,
        internalCost: { light: 0.4, dark: 2.5 },
        priceTiers: [
            { maxQty: 1, light: { single: 17, double: 25 }, dark: { single: 21, double: 33 } },
            { maxQty: 5, light: { single: 15, double: 22 }, dark: { single: 19, double: 30 } },
            { maxQty: 11, light: { single: 12, double: 18 }, dark: { single: 16, double: 25 } },
            { maxQty: 23, light: { single: 10, double: 15 }, dark: { single: 14, double: 21 } },
        ],
        marginWarnFloorPct: 30,
    },
    embroidery: {
        minQty: 12,
        baseTiers: {
            lc: [
                { minQty: 144, rate: 6.5 },
                { minQty: 72, rate: 8.0 },
                { minQty: 48, rate: 9.0 },
                { minQty: 24, rate: 10.0 },
                { minQty: 12, rate: 12.0 },
                // Lookup helper requires a minQty:0 fallback. Engine throws
                // BelowMinimumError below 12; row is unreachable in practice.
                { minQty: 0, rate: 12.0 },
            ],
            hat: [
                { minQty: 144, rate: 7.5 },
                { minQty: 72, rate: 9.0 },
                { minQty: 48, rate: 10.0 },
                { minQty: 24, rate: 12.0 },
                { minQty: 12, rate: 14.0 },
                { minQty: 0, rate: 14.0 },
            ],
            back: [
                { minQty: 144, rate: 14.0 },
                { minQty: 72, rate: 17.0 },
                { minQty: 48, rate: 20.0 },
                { minQty: 24, rate: 24.0 },
                { minQty: 12, rate: 28.0 },
                { minQty: 0, rate: 28.0 },
            ],
        },
        complexityMultiplier: { simple: 1, standard: 1.1, complex: 1.3 },
        additionalLocationTiers: [
            { minQty: 144, rate: 4.0 },
            { minQty: 72, rate: 5.0 },
            { minQty: 48, rate: 6.0 },
            { minQty: 24, rate: 7.0 },
            { minQty: 12, rate: 8.0 },
            { minQty: 0, rate: 8.0 },
        ],
        nameTiers: [
            { minQty: 144, rate: 4.0 },
            { minQty: 72, rate: 5.0 },
            { minQty: 48, rate: 6.0 },
            { minQty: 24, rate: 7.0 },
            { minQty: 12, rate: 8.0 },
            { minQty: 0, rate: 8.0 },
        ],
        numberMultiplier: 0.75,
        stitchTiers: {
            simpleMaxK: 6,
            standardMaxK: 12,
            complexRepresentativeK: 20,
            standardRepresentativeK: 12,
            simpleRepresentativeK: 6,
        },
        costDefaults: {
            costPerThousandStitches: 0.2,
            overheadPerPiece: 0.5,
            threadPerPiece: 0.35,
        },
        digiCosts: {
            none: [0, 0],
            dst: [0, 0],
            lc: [16.99, 22.99],
            hat: [16.99, 22.99],
            hat3d: [16.99, 22.99],
            beanie: [16.99, 22.99],
            bigfb: [32.99, 38.99],
            lchat: [19.99, 24.99],
            combo3: [24.99, 27.99],
        },
        digiCustomerMarkup: 10.0,
    },
    routing: {
        thresholds: {
            dtgMaxQty: 11,
            dtfPpMinForSp: 48,
            spSingleSideMaxColorsClear: 3,
            doubleSidedVolumeBreak: 200,
            anyServiceVolumeBreak: 500,
        },
    },
};
//# sourceMappingURL=defaults.js.map